import { useState, useEffect } from "react";
import useAppStore from "src/stores/appStore";
import {
  handleDraftTripletsSelector,
  setInfoPopoversSelector
} from "src/stores/selectors/tripletsSelectors";
import { isUpdatingCollectionSelector } from "src/stores/selectors/collectionsSelectors";
import { previewDraftBuildResponseSelector } from "src/stores/selectors/previewSelectors";
import { isReadOnlyModeSelector, selectedAssistantSelector } from "src/stores/selectors/appSelectors";
import { DONT_SHOW_AGAIN_STORAGE } from "../header-types/status-header/DraftPublishing/DraftHeaderConsts";
import { SEVERITY_VARIANTS, BUILD_STATUSES, PIPELINE_STATUSES } from "@consts";
import { useDraft } from "src/queries/hooks/assistants";
import { useFetchDashboardSettings } from "src/queries/hooks/settings";
import { notifyPublishChanges } from "src/analytics/notifyCustomActions";
import { PUBLISH_BUTTON_TOOLTIP } from "./PublishButton.consts";
import { queryClient } from "src/lib/react-query/queryClient";
import { FETCH_QUERY_KEYS } from "src/queries/consts";

export const usePublishButton = () => {
  const selectedAssistant = useAppStore(selectedAssistantSelector);
  const [publishConfirmationOpen, setPublishConfirmationOpen] = useState(false);
  const [publishCurrentTripletsToDraft] = useAppStore(handleDraftTripletsSelector);
  const isReadOnly = useAppStore(isReadOnlyModeSelector);
  const setInfoPopovers = useAppStore(setInfoPopoversSelector);
  const buildDraftResponse = useAppStore(previewDraftBuildResponseSelector);
  const isUpdatingCollection = useAppStore(isUpdatingCollectionSelector);
  const { data: draft, isError: isDraftError } = useDraft();
  const [snackbarAlert, setSnackbarAlert] = useState({
    open: false,
    message: "",
    severity: ""
  });
  const [isLoading, setIsLoading] = useState(false);
  const dashboardSettings = useFetchDashboardSettings()?.data;

  const isPublishing = draft?.pipelineStatus === PIPELINE_STATUSES.PUBLISHED;
  const isPublishFailed = draft?.pipelineStatus === PIPELINE_STATUSES.FAILED;
  const shouldBuild = !draft || isUpdatingCollection || buildDraftResponse?.status !== BUILD_STATUSES.LIVE;

  const isPublishDisabled = dashboardSettings?.disablePublish
        || shouldBuild
        || isReadOnly
        || isPublishing
        || isLoading
        || isDraftError
        || isPublishFailed;

  useEffect(() => {
    if (draft?.pipelineStatus === PIPELINE_STATUSES.PUBLISHED) {
      setIsLoading(false);
    }
  }, [draft?.pipelineStatus]);

  const getButtonTooltip = () => {
    if (!isPublishDisabled) return "";
    if (isPublishing) return PUBLISH_BUTTON_TOOLTIP.IN_PROGRESS;
    if (isPublishFailed) return PUBLISH_BUTTON_TOOLTIP.PUBLISH_FAILED;
    if (shouldBuild) return PUBLISH_BUTTON_TOOLTIP.BUILD_NEEDED;

    return "";
  };

  const handleClickPublish = async ({ changeDescription }) => {
    setIsLoading(true);
    setSnackbarAlert({
      open: true,
      message: "Publishing changes",
      severity: SEVERITY_VARIANTS.SUCCESS
    });
    await publishCurrentTripletsToDraft(changeDescription);
    queryClient.refetchQueries({ queryKey: [FETCH_QUERY_KEYS.DRAFT] });
    setPublishConfirmationOpen(false);
    setInfoPopovers(DONT_SHOW_AGAIN_STORAGE.SET_LIVE, true);
    handleCloseSnackbar();
  };

  const handleCloseSnackbar = () => setSnackbarAlert({ ...snackbarAlert, open: false });

  const openPublishDialog = () => {
    notifyPublishChanges("Publish", {
      assistantId: selectedAssistant.assistantId,
      assistantChannel: selectedAssistant.channel
    });
    setPublishConfirmationOpen(true);
  };

  const closePublishDialog = () => setPublishConfirmationOpen(false);

  return {
    publishConfirmationOpen,
    isPublishDisabled,
    isPublishing,
    isLoading,
    getButtonTooltip,
    handleClickPublish,
    openPublishDialog,
    closePublishDialog
  };
};
